import { type ReactNode, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Label,
  LabeledControl,
  Radio,
  Select,
} from '@zillow/constellation-10';
import { Eye } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useRenterProfile, useUpdateRenterProfile } from '@/src/lib/hooks/query';
import type { RenterProfileFormData } from '@/src/lib/utils/renterProfileTransforms';
import {
  cardBg,
  cardBody,
  cardHeader,
  description,
  embeddedSection,
  embeddedSectionHeader,
  form,
  helperText,
  infoBanner,
  infoBannerText,
  infoBannerTitle,
  radioGroup,
  saveRow,
  section,
  sectionLabel,
} from './renterProfile.styles';

function incomeToSelectValue(income?: number): string {
  if (income === undefined) return '';
  if (income <= 30000) return '30000';
  if (income <= 50000) return '50000';
  if (income <= 75000) return '75000';
  if (income <= 100000) return '100000';
  if (income <= 150000) return '150000';
  return '200000';
}

function creditScoreToSelectValue(min?: number, max?: number): string {
  if (min === undefined || max === undefined) return '';
  if (max <= 579) return '300-579';
  if (max <= 619) return '580-619';
  if (max <= 719) return '620-719';
  return '720-850';
}

function parseCreditScoreSelect(value: string): { min: number; max: number } | null {
  const parts = value.split('-');
  if (parts.length !== 2) return null;
  return { min: Number(parts[0]), max: Number(parts[1]) };
}

function populateFormFromApi(data: RenterProfileFormData) {
  return {
    moveInDate: data.moveInDate ?? '',
    flexibleMoveIn: data.moveInTimeFrame === 'flexible',
    leaseDuration: data.leaseDuration != null ? String(data.leaseDuration) : '',
    annualIncome: incomeToSelectValue(data.incomeYearly),
    creditScore: creditScoreToSelectValue(data.creditScoreMin, data.creditScoreMax),
    occupants: data.numOccupants != null ? String(data.numOccupants) : '',
    bedrooms: data.numBeds != null ? String(data.numBeds) : '',
    pets: data.desiredPets === 'true' ? 'yes' : 'no',
    parking: data.desiredParking === 'true' ? 'yes' : 'no',
  };
}

type RenterProfileFormProps = {
  onSaved?: () => void;
  onCancel?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  variant?: 'standalone' | 'embedded';
};

type RenterProfileSectionProps = {
  children: ReactNode;
  title: string;
  variant: 'standalone' | 'embedded';
};

function RenterProfileSection({ children, title, variant }: RenterProfileSectionProps) {
  if (variant === 'embedded') {
    return (
      <section className={embeddedSection}>
        <h3 className={embeddedSectionHeader}>{title}</h3>
        <div className={cardBody}>{children}</div>
      </section>
    );
  }

  return (
    <Card elevated={false} className={cardBg}>
      <Card.Header>
        <div className={cardHeader}>{title}</div>
      </Card.Header>
      <Card.Body className={cardBody}>{children}</Card.Body>
    </Card>
  );
}

export function RenterProfileForm({
  onSaved,
  onCancel,
  submitLabel,
  cancelLabel,
  variant = 'standalone',
}: RenterProfileFormProps = {}) {
  const t = useTranslations('renter');
  const [moveInDate, setMoveInDate] = useState('');
  const [flexibleMoveIn, setFlexibleMoveIn] = useState(false);
  const [leaseDuration, setLeaseDuration] = useState('');
  const [annualIncome, setAnnualIncome] = useState('');
  const [creditScore, setCreditScore] = useState('');
  const [occupants, setOccupants] = useState('');
  const [bedrooms, setBedrooms] = useState('');
  const [pets, setPets] = useState('no');
  const [parking, setParking] = useState('no');

  const { data: profileData, isLoading } = useRenterProfile();
  const updateRenterProfile = useUpdateRenterProfile();

  // Populate form when API data loads
  useEffect(() => {
    if (!profileData) return;
    const values = populateFormFromApi(profileData);
    setMoveInDate(values.moveInDate);
    setFlexibleMoveIn(values.flexibleMoveIn);
    setLeaseDuration(values.leaseDuration);
    setAnnualIncome(values.annualIncome);
    setCreditScore(values.creditScore);
    setOccupants(values.occupants);
    setBedrooms(values.bedrooms);
    setPets(values.pets);
    setParking(values.parking);
  }, [profileData]);

  const handleSave = () => {
    const creditScoreParsed = parseCreditScoreSelect(creditScore);

    updateRenterProfile.mutate(
      {
        moveInDate: moveInDate || undefined,
        moveInTimeFrame: flexibleMoveIn ? 'flexible' : undefined,
        leaseLengthMonths: leaseDuration ? Number(leaseDuration) : undefined,
        incomeYearly: annualIncome ? Number(annualIncome) : undefined,
        creditScoreMin: creditScoreParsed?.min,
        creditScoreMax: creditScoreParsed?.max,
        numOccupants: occupants ? Number(occupants) : undefined,
        numBedrooms: bedrooms !== '' ? Number(bedrooms) : undefined,
        desiredPets: pets === 'yes' ? 'true' : 'false',
        desiredParking: parking === 'yes' ? 'true' : 'false',
      },
      { onSuccess: onSaved },
    );
  };

  return (
    <div>
      <p className={description}>{t('description')}</p>

      <div className={infoBanner}>
        <div className={infoBannerTitle}>
          <LucideIcon icon={Eye} size="sm" />
          {t('sharedTitle')}
        </div>
        <p className={infoBannerText}>{t('sharedText')}</p>
      </div>

      <div className={form}>
        <RenterProfileSection title={t('moveInPreferences')} variant={variant}>
          <div className={section}>
            <p className={sectionLabel}>{t('preferredMoveInDate')}</p>
            <Input
              type="date"
              name="moveInDate"
              value={moveInDate}
              onChange={(e) => setMoveInDate(e.target.value)}
              disabled={isLoading}
            />
            <LabeledControl
              label={<Label>{t('flexibleMoveIn')}</Label>}
              control={
                <Checkbox
                  checked={flexibleMoveIn}
                  onChange={(e) => setFlexibleMoveIn(e.target.checked)}
                  disabled={isLoading}
                />
              }
            />
          </div>

          <div className={section}>
            <FormField
              label={<Label>{t('preferredLeaseDuration')}</Label>}
              control={
                <Select value={leaseDuration} onChange={(e) => setLeaseDuration(e.target.value)} disabled={isLoading}>
                  <option value="">{t('selectLeaseDuration')}</option>
                  <option value="1">{t('monthToMonth')}</option>
                  <option value="6">{t('sixMonths')}</option>
                  <option value="12">{t('twelveMonths')}</option>
                  <option value="13">{t('thirteenPlusMonths')}</option>
                </Select>
              }
            />
          </div>
        </RenterProfileSection>

        <RenterProfileSection title={t('financialInformation')} variant={variant}>
          <div className={section}>
            <FormField
              label={<Label>{t('annualIncome')}</Label>}
              control={
                <Select value={annualIncome} onChange={(e) => setAnnualIncome(e.target.value)} disabled={isLoading}>
                  <option value="">{t('selectIncomeRange')}</option>
                  <option value="30000">{t('incomeUnder30')}</option>
                  <option value="50000">{t('income30to50')}</option>
                  <option value="75000">{t('income50to75')}</option>
                  <option value="100000">{t('income75to100')}</option>
                  <option value="150000">{t('income100to150')}</option>
                  <option value="200000">{t('income150Plus')}</option>
                </Select>
              }
            />
            <p className={helperText}>{t('incomeHelper')}</p>
          </div>

          <div className={section}>
            <FormField
              label={<Label>{t('creditScore')}</Label>}
              control={
                <Select value={creditScore} onChange={(e) => setCreditScore(e.target.value)} disabled={isLoading}>
                  <option value="">{t('selectCreditRange')}</option>
                  <option value="300-579">{t('credit300')}</option>
                  <option value="580-619">{t('credit580')}</option>
                  <option value="620-719">{t('credit620')}</option>
                  <option value="720-850">{t('credit720')}</option>
                </Select>
              }
            />
            <p className={helperText}>{t('creditHelper')}</p>
          </div>
        </RenterProfileSection>

        <RenterProfileSection title={t('household')} variant={variant}>
          <div className={section}>
            <FormField
              label={<Label>{t('numberOfPeople')}</Label>}
              control={
                <Select value={occupants} onChange={(e) => setOccupants(e.target.value)} disabled={isLoading}>
                  <option value="">{t('selectOccupants')}</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">{t('fivePlus')}</option>
                </Select>
              }
            />
            <p className={helperText}>{t('occupantsHelper')}</p>
          </div>

          <div className={section}>
            <FormField
              label={<Label>{t('bedroomsDesired')}</Label>}
              control={
                <Select value={bedrooms} onChange={(e) => setBedrooms(e.target.value)} disabled={isLoading}>
                  <option value="">{t('selectBedrooms')}</option>
                  <option value="0">{t('studio')}</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">{t('fourPlus')}</option>
                </Select>
              }
            />
          </div>

          <div className={section}>
            <p className={sectionLabel}>{t('pets')}</p>
            <div className={radioGroup}>
              <LabeledControl
                label={<Label>{t('yes')}</Label>}
                control={
                  <Radio
                    name="pets"
                    value="yes"
                    checked={pets === 'yes'}
                    onChange={() => setPets('yes')}
                    disabled={isLoading}
                  />
                }
              />
              <LabeledControl
                label={<Label>{t('no')}</Label>}
                control={
                  <Radio
                    name="pets"
                    value="no"
                    checked={pets === 'no'}
                    onChange={() => setPets('no')}
                    disabled={isLoading}
                  />
                }
              />
            </div>
            <p className={helperText}>{t('petsHelper')}</p>
          </div>

          <div className={section}>
            <p className={sectionLabel}>{t('parkingNeeds')}</p>
            <div className={radioGroup}>
              <LabeledControl
                label={<Label>{t('yes')}</Label>}
                control={
                  <Radio
                    name="parking"
                    value="yes"
                    checked={parking === 'yes'}
                    onChange={() => setParking('yes')}
                    disabled={isLoading}
                  />
                }
              />
              <LabeledControl
                label={<Label>{t('no')}</Label>}
                control={
                  <Radio
                    name="parking"
                    value="no"
                    checked={parking === 'no'}
                    onChange={() => setParking('no')}
                    disabled={isLoading}
                  />
                }
              />
            </div>
          </div>
        </RenterProfileSection>

        <div className={saveRow}>
          {onCancel && (
            <Button
              tone="neutral"
              emphasis="outlined"
              type="button"
              onClick={onCancel}
              disabled={updateRenterProfile.isPending}
            >
              {cancelLabel ?? t('skip')}
            </Button>
          )}
          <Button
            tone="brand"
            emphasis="filled"
            type="button"
            onClick={handleSave}
            disabled={isLoading || updateRenterProfile.isPending}
          >
            {updateRenterProfile.isPending ? t('saving') : (submitLabel ?? t('save'))}
          </Button>
        </div>
      </div>
    </div>
  );
}
